import { useRef, useState } from 'react';
import { AddOutlined, ArrowBackOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Divider, Stack } from '@mui/material';
import { DialogSurface, FlowSteps, FormGrid, FormGridItem, FormSection, FormTextField, useConfirmation } from '../../../design-system';
import { useUnsavedChangesGuard } from '../../../shared/useUnsavedChangesGuard';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { StaffingOperationsPort } from '../operations/port';
import { staffingPlanInput, validateStaffingPlan, type StaffingPlan, type StaffingPlanInput } from '../operations/planModel';
import { fixtureOrganizations, fixtureJobs, fixtureJobLevels, fixtureLocations } from '../fixtures/staffingPageFixtures';
import { errorStatus } from '../model/staffingPagePresentation';
import { StaffingPlanOrganizationFields, StaffingPlanWorkerFields } from './StaffingPlanFields';
import { StaffingPlanReview } from './StaffingPlanReview';
import { saveFixtureStaffingPlan } from '../fixtures/staffingPlanFixtures';

export function StaffingPlanComposer({ initial, initialInput, port, fixtureMode, onClose, onSaved, onReload }: {
  initial?: StaffingPlan; initialInput?: StaffingPlanInput; port: StaffingOperationsPort; fixtureMode: boolean;
  onClose: () => void; onSaved: (plan: StaffingPlan, submitted: boolean) => void;
  onReload: (plan: StaffingPlan) => void;
}) {
  const [baseline, setBaseline] = useState(initial);
  const [draft, setDraft] = useState<StaffingPlanInput>(() => initial || initialInput ? staffingPlanInput((initial ?? initialInput)!) : { title: '', effectiveDate: '', reason: '', organizationChanges: [], workerChanges: [] });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<StaffingPlan>();
  const [frozen, setFrozen] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [reviewReady, setReviewReady] = useState(false);
  const { confirm, confirmationDialog } = useConfirmation();
  const original = saved ?? baseline ?? initialInput ?? { title: '', effectiveDate: '', reason: '', organizationChanges: [], workerChanges: [] };
  const dirty = JSON.stringify(staffingPlanInput(draft)) !== JSON.stringify(staffingPlanInput(original));
  useUnsavedChangesGuard({ dirty, busy });
  const requestClose = async () => {
    if (busy) return;
    if (dirty && !await confirm({ title: '입력한 변경을 버릴까요?', description: '저장하지 않은 발령안 입력은 사라집니다.', confirmLabel: '변경 버리기', cancelLabel: '계속 입력', intent: 'discard' })) return;
    onClose();
  };
  const keys = useRef({ save: crypto.randomUUID(), submit: crypto.randomUUID() });
  const attempt = useRef<{ input: StaffingPlanInput; submitted: boolean } | undefined>(undefined);
  const references = useAsyncResource(async () => {
    if (!draft.effectiveDate) return { organizations: [], jobProfiles: [], jobLevels: [], locations: [] };
    if (fixtureMode) return { organizations: fixtureOrganizations, jobProfiles: fixtureJobs, jobLevels: fixtureJobLevels, locations: fixtureLocations };
    const [organizations, jobProfiles, jobLevels, locations] = await Promise.all([port.listOrganizations(draft.effectiveDate), port.listJobProfiles(draft.effectiveDate), port.listJobLevels(draft.effectiveDate), port.listLocations(draft.effectiveDate)]);
    return { organizations, jobProfiles, jobLevels, locations };
  }, [port, fixtureMode, draft.effectiveDate]);
  const disabled = busy || frozen;
  const next = () => {
    const validation = validateStaffingPlan(draft, step === 0);
    if (validation) { setError(validation); return; }
    if (step === 1 && references.data?.organizations.length && draft.organizationChanges.some((row) => row.operation === 'CREATE' && !row.parent)) {
      setError('신설 조직의 상위 조직을 선택해 주세요.'); return;
    }
    setError(''); setStep((value) => value + 1);
  };
  const persist = async (submitted: boolean) => {
    if (busy || conflict) return;
    const validation = validateStaffingPlan(draft);
    if (validation) { setError(validation); return; }
    setBusy(true); setError(''); setFrozen(true);
    attempt.current ??= { input: staffingPlanInput(draft), submitted };
    let persisted = saved;
    try {
      let plan = saved;
      if (!plan) {
        if (fixtureMode) {
          const now = new Date().toISOString();
          plan = { ...attempt.current.input, id: baseline?.id ?? `fixture-plan-${crypto.randomUUID()}`, version: (baseline?.version ?? 0) + 1, status: 'DRAFT', createdAt: baseline?.createdAt ?? now, updatedAt: now };
        } else {
          plan = baseline ? await port.updatePlan(baseline.id, attempt.current.input, baseline.version, keys.current.save) : await port.createPlan(attempt.current.input, keys.current.save);
        }
        setSaved(plan);
        persisted = plan;
      }
      if (attempt.current.submitted) {
        plan = fixtureMode ? { ...plan, status: 'SUBMITTED', updatedAt: new Date().toISOString() } : await port.submitPlan(plan.id, plan.version, keys.current.submit);
      }
      if (fixtureMode) saveFixtureStaffingPlan(plan);
      onSaved(plan, attempt.current.submitted);
    } catch (cause) {
      const status = errorStatus(cause);
      if (status === 409) {
        setConflict(true);
        setError('최신 발령안을 확인해 주세요.');
      } else if (status && status >= 400 && status < 500) {
        setError(cause instanceof Error ? cause.message : '입력 내용을 확인해 주세요.');
        if (!persisted) { setFrozen(false); attempt.current = undefined; keys.current = { save: crypto.randomUUID(), submit: crypto.randomUUID() }; }
      } else {
        setError('요청 결과를 확인하지 못했습니다. 같은 내용으로 다시 시도해 주세요.');
      }
    } finally { setBusy(false); }
  };
  const reloadConflict = async () => {
    const id = saved?.id ?? baseline?.id;
    if (!id) { setError('요청이 충돌했습니다. 발령안 목록에서 저장 여부를 확인한 뒤 다시 작성해 주세요.'); return; }
    setBusy(true);
    try {
      const latest = await port.getPlan(id);
      if (latest.status !== 'DRAFT') { onReload(latest); return; }
      setBaseline(latest); setDraft(staffingPlanInput(latest)); setSaved(undefined); setFrozen(false); setConflict(false); setStep(0);
      setError('최신 초안을 불러왔습니다. 내용을 확인하고 다시 저장해 주세요.');
      attempt.current = undefined; keys.current = { save: crypto.randomUUID(), submit: crypto.randomUUID() };
    } catch { setError('최신 발령안을 불러오지 못했습니다. 다시 시도해 주세요.'); }
    finally { setBusy(false); }
  };
  return <><DialogSurface open onClose={() => void requestClose()} busy={busy} title={initial ? '발령안 수정' : '발령안 작성'} size="large" mobilePresentation="fullscreen" contentDividers
    help="조직 변경과 여러 구성원의 발령을 하나의 발령안으로 작성할 수 있습니다. 새 조직도 이동할 조직으로 선택할 수 있습니다. 승인 완료 후 시행일 기준으로 함께 반영됩니다."
    headerContent={<FlowSteps activeStep={step} ariaLabel="발령안 작성 단계" steps={['기본 정보', '변경 내용', '검토']} />}
    actions={<Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap' }}>
      <Button onClick={() => void requestClose()} disabled={busy}>취소</Button>
      {step > 0 && <Button startIcon={<ArrowBackOutlined />} onClick={() => { setError(''); setStep((value) => value - 1); }} disabled={disabled}>이전</Button>}
      <Box sx={{ flex: 1 }} />
      {step < 2 ? <Button variant="contained" endIcon={<ArrowForwardOutlined />} onClick={next} disabled={busy || (step === 1 && (references.loading || Boolean(references.error)))}>{step === 0 ? '변경 내용 입력' : '발령안 검토'}</Button> : <>
        <Button variant="outlined" onClick={() => void persist(false)} disabled={busy || conflict || !reviewReady || Boolean(attempt.current?.submitted)}>{busy ? '처리 중…' : frozen && !saved ? '저장 재시도' : '초안 저장'}</Button>
        <Button variant="contained" onClick={() => void persist(true)} disabled={busy || conflict || !reviewReady || Boolean(frozen && attempt.current && !attempt.current.submitted)}>{busy ? '처리 중…' : frozen ? '승인 요청 재시도' : '승인 요청'}</Button>
      </>}
    </Stack>}>
    <Stack spacing={2}>
      {error && <Alert severity="error" action={conflict && (saved || initial) ? <Button onClick={() => void reloadConflict()} disabled={busy}>최신 발령안 확인</Button> : undefined}>{conflict ? '다른 요청에서 발령안이 변경되었거나 제출 조건이 달라졌습니다. 최신 발령안을 확인해 주세요.' : error}</Alert>}
      {frozen && !busy && !conflict && <Alert severity="warning">{saved ? '초안은 저장됐습니다. 같은 발령안으로 승인 요청을 다시 시도할 수 있습니다.' : '저장 결과를 확인할 때까지 입력 내용이 고정됩니다.'}</Alert>}
      {step === 0 && <FormSection heading="기본 정보"><FormGrid>
        <FormGridItem wide><FormTextField required label="발령안 제목" value={draft.title} disabled={disabled} onChange={(event) => setDraft({ ...draft, title: event.target.value })} /></FormGridItem>
        <FormTextField required type="date" label="시행일" value={draft.effectiveDate} disabled={disabled} onChange={(event) => setDraft({ ...draft, effectiveDate: event.target.value })} />
        <FormGridItem wide><FormTextField required multiline minRows={3} label="발령 사유" value={draft.reason} disabled={disabled} onChange={(event) => setDraft({ ...draft, reason: event.target.value })} /></FormGridItem>
      </FormGrid></FormSection>}
      {step === 1 && <>
        {references.error && <Alert severity="error" action={<Button onClick={references.reload}>다시 시도</Button>}>시행일 기준 정보를 불러오지 못했습니다.</Alert>}
        {references.data && <>
          {draft.organizationChanges.map((row, index) => <StaffingPlanOrganizationFields key={row.key} row={row} index={index} plan={draft} references={references.data!} port={port} fixtureMode={fixtureMode} disabled={disabled || references.loading}
            onChange={(nextRow) => setDraft({ ...draft, organizationChanges: draft.organizationChanges.map((current) => current.key === row.key ? nextRow : current) })}
            onRemove={() => setDraft({ ...draft, organizationChanges: draft.organizationChanges.filter((current) => current.key !== row.key) })} />)}
          <Button variant="outlined" startIcon={<AddOutlined />} disabled={disabled || references.loading} onClick={() => {
            const root = references.data?.organizations.find((organization) => !organization.parentId);
            setDraft({ ...draft, organizationChanges: [...draft.organizationChanges, { key: crypto.randomUUID(), operation: 'CREATE', parent: root ? { id: root.id } : undefined }] });
          }}>조직 변경 추가</Button>
          <Divider />
          {draft.workerChanges.map((row, index) => <StaffingPlanWorkerFields key={row.key} row={row} index={index} plan={draft} references={references.data!} port={port} fixtureMode={fixtureMode} disabled={disabled || references.loading}
            onChange={(nextRow) => setDraft({ ...draft, workerChanges: draft.workerChanges.map((current) => current.key === row.key ? nextRow : current) })}
            onRemove={() => setDraft({ ...draft, workerChanges: draft.workerChanges.filter((current) => current.key !== row.key) })} />)}
          <Button variant="outlined" startIcon={<AddOutlined />} disabled={disabled || references.loading} onClick={() => setDraft({ ...draft, workerChanges: [...draft.workerChanges, { key: crypto.randomUUID(), workerId: '', operation: 'JOB_CHANGE' }] })}>구성원 발령 추가</Button>
        </>}
      </>}
      {step === 2 && <StaffingPlanReview plan={draft} port={port} fixtureMode={fixtureMode} onReady={setReviewReady} />}
    </Stack>
  </DialogSurface>{confirmationDialog}</>;
}
